import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { errMsg } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { formatDateTime } from "@/lib/format";
import { ShieldCheck, Search, RefreshCw, ArrowLeft } from "lucide-react";

const C = { navy: "#1C4358", slate: "#4B5563", charcoal: "#252221", line: "#E6EDF2", red: "#9C1E22", amber: "#B7791F", green: "#2F7D5A" };

const STATUS_TONE = { success: C.green, denied: C.red, failed: C.amber };
const RANGES = [
  { key: "1d", label: "24h", days: 1 },
  { key: "7d", label: "7 days", days: 7 },
  { key: "30d", label: "30 days", days: 30 },
  { key: "all", label: "All", days: null },
];

export default function SecurityAuditLogs() {
  const navigate = useNavigate();
  const [cats, setCats] = useState([]);
  const [category, setCategory] = useState("all");
  const [status, setStatus] = useState("all");
  const [range, setRange] = useState("7d");
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [scope, setScope] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/security/audit-categories").then(({ data }) => setCats(data.categories || [])).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    const params = { limit: 300 };
    if (category !== "all") params.category = category;
    if (status !== "all") params.status = status;
    if (q.trim()) params.q = q.trim();
    const r = RANGES.find((x) => x.key === range);
    if (r?.days) params.date_from = new Date(Date.now() - r.days * 864e5).toISOString();
    try {
      const { data } = await api.get("/security/audit-logs", { params });
      setRows(data.items || []);
      setCounts(data.counts || {});
      setScope(data.scope || "");
    } catch (e) { toast.error(errMsg(e)); }
    finally { setLoading(false); }
  }, [category, status, range, q]);

  useEffect(() => { load(); }, [category, status, range]);

  const catLabel = (k) => cats.find((c) => c.key === k)?.label || k;

  return (
    <div className="max-w-6xl mx-auto pb-16" data-testid="security-audit-logs-page">
      <button onClick={() => navigate("/security")} className="text-[12.5px] mb-2 inline-flex items-center gap-1" style={{ color: C.slate }} data-testid="audit-back-security">
        <ArrowLeft size={13} /> Security & Compliance
      </button>
      <div className="flex items-start justify-between gap-3 flex-wrap mb-1">
        <h1 className="font-display text-2xl font-bold flex items-center gap-2" style={{ color: C.charcoal }}>
          <ShieldCheck size={22} style={{ color: C.navy }} /> Audit Logs
        </h1>
        <Button variant="outline" size="sm" onClick={load} className="h-8 gap-1 border-[#D6E0E7] text-[#1C4358]" data-testid="audit-refresh">
          <RefreshCw size={13} /> Refresh
        </Button>
      </div>
      <p className="text-[13px] mb-4" style={{ color: C.slate }}>
        Who did what, when — authentication, access, documents, guests, comms, consent, privacy & more.
        {scope === "scoped" && <span style={{ color: C.amber }}> Scoped to your branches.</span>}
      </p>

      {/* filters */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="relative">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2" style={{ color: C.slate }} />
          <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Search action, user, entity…" className="h-8 pl-8 w-[240px] text-[13px]" data-testid="audit-search" />
        </div>
        <div className="inline-flex rounded-lg border p-0.5" style={{ borderColor: C.line }}>
          {["all", "success", "denied", "failed"].map((s) => (
            <button key={s} data-testid={`audit-status-${s}`} onClick={() => setStatus(s)}
              className="px-2.5 h-7 text-[12px] rounded-md capitalize"
              style={{ background: status === s ? C.navy : "transparent", color: status === s ? "#fff" : C.slate }}>{s}</button>
          ))}
        </div>
        <div className="inline-flex rounded-lg border p-0.5" style={{ borderColor: C.line }}>
          {RANGES.map((r) => (
            <button key={r.key} data-testid={`audit-range-${r.key}`} onClick={() => setRange(r.key)}
              className="px-2.5 h-7 text-[12px] rounded-md"
              style={{ background: range === r.key ? C.navy : "transparent", color: range === r.key ? "#fff" : C.slate }}>{r.label}</button>
          ))}
        </div>
      </div>

      {/* category chips */}
      <div className="flex flex-wrap gap-1.5 mb-4" data-testid="audit-category-chips">
        <button onClick={() => setCategory("all")} data-testid="audit-cat-all"
          className="text-[11.5px] px-2.5 py-1 rounded-full border"
          style={{ borderColor: category === "all" ? C.navy : C.line, background: category === "all" ? "rgba(28,67,88,0.08)" : "#fff", color: category === "all" ? C.navy : C.slate }}>
          All
        </button>
        {cats.map((c) => (
          <button key={c.key} data-testid={`audit-cat-${c.key}`} onClick={() => setCategory(c.key)}
            className="text-[11.5px] px-2.5 py-1 rounded-full border"
            style={{ borderColor: category === c.key ? C.navy : C.line, background: category === c.key ? "rgba(28,67,88,0.08)" : "#fff", color: category === c.key ? C.navy : C.slate }}>
            {c.label}{counts[c.key] ? ` · ${counts[c.key]}` : ""}
          </button>
        ))}
      </div>

      <Card className="border-[#E6EDF2] overflow-hidden">
        {loading ? (
          <div className="p-4 space-y-2">{[1, 2, 3, 4, 5].map((i) => <Skeleton key={i} className="h-9" />)}</div>
        ) : rows.length === 0 ? (
          <div className="p-8 text-center text-[13px]" style={{ color: C.slate }} data-testid="audit-empty">No audit entries for these filters.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[12.5px]">
              <thead>
                <tr className="text-left" style={{ color: C.slate, borderBottom: `1px solid ${C.line}` }}>
                  <th className="py-2 px-3 font-medium">When</th>
                  <th className="py-2 px-3 font-medium">Actor</th>
                  <th className="py-2 px-3 font-medium">Action</th>
                  <th className="py-2 px-3 font-medium">Category</th>
                  <th className="py-2 px-3 font-medium">Status</th>
                  <th className="py-2 px-3 font-medium">Entity</th>
                  <th className="py-2 px-3 font-medium">IP</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} data-testid="audit-row" className="border-b last:border-0" style={{ borderColor: C.line }}>
                    <td className="py-2 px-3 whitespace-nowrap" style={{ color: C.slate }}>{formatDateTime(r.created_at)}</td>
                    <td className="py-2 px-3">
                      <div style={{ color: C.charcoal }}>{r.user_email || (r.user_id ? "—" : "system")}</div>
                      {r.user_role && <div className="text-[10.5px]" style={{ color: C.slate }}>{String(r.user_role).replace(/_/g, " ")}</div>}
                    </td>
                    <td className="py-2 px-3" style={{ color: C.charcoal }}>{String(r.action || "").replace(/_/g, " ")}</td>
                    <td className="py-2 px-3" style={{ color: C.slate }}>{catLabel(r.category)}</td>
                    <td className="py-2 px-3">
                      <span className="text-[10.5px] px-1.5 py-0.5 rounded-full capitalize"
                        style={{ background: `${STATUS_TONE[r.status] || C.slate}1A`, color: STATUS_TONE[r.status] || C.slate }} data-testid="audit-status-badge">
                        {r.status || "success"}
                      </span>
                    </td>
                    <td className="py-2 px-3" style={{ color: C.slate }}>
                      {r.resource_type ? <span>{r.resource_type}{r.resource_id ? ` · ${String(r.resource_id).slice(0, 8)}` : ""}</span> : "—"}
                    </td>
                    <td className="py-2 px-3 whitespace-nowrap" style={{ color: C.slate }}>{r.ip || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
